// Copyright ©️ 2026 eVolve MEP, LLC

import type { SavedGridStateJsonString } from '@evolvemep/api-ts';
import type { ColumnSizeState, GridState } from 'ag-grid-enterprise';

import { getSavedGridState } from 'helpers/ag-grid/TableViewPanel/getSavedGridState';
import type { SavedGridState, SavedGridStateKey } from 'helpers/ag-grid/TableViewPanel/types';
import type { WithRequiredIsh } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';

export const parseGridStateJson = (jsonString: SavedGridStateJsonString): SavedGridState | null => {
  if (!jsonString.trim()) return null;
  try {
    return JSON.parse(jsonString) as SavedGridState;
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error('Failed to parse grid state JSON string:', error);
    return null;
  }
};

export const stringifyGridStateJson = (gridState: GridState): SavedGridStateJsonString => {
  const savedGridState = getSavedGridState(gridState);
  return JSON.stringify(savedGridState) as SavedGridStateJsonString;
};

type NormalizedGridState = { [K in SavedGridStateKey]-?: WithRequiredIsh<NonNullable<SavedGridState[K]>> };

/** Normalizes a saved grid state specifically for comparison via {@link gridStatesEqual} */
const normalizeGridState = (gridState: SavedGridState | null): NormalizedGridState => ({
  columnOrder: { orderedColIds: gridState?.columnOrder?.orderedColIds ?? [] },
  columnPinning: {
    leftColIds: gridState?.columnPinning?.leftColIds.toSorted() ?? [],
    rightColIds: gridState?.columnPinning?.rightColIds.toSorted() ?? [],
  },
  columnSizing: {
    columnSizingModel:
      gridState?.columnSizing?.columnSizingModel
        .map(({ colId, width, flex }): WithRequiredIsh<ColumnSizeState> => ({
          colId,
          // A flex column's width changes whenever the window is resized, so only its flex is meaningful.
          width: isNil(flex) ? width : undefined,
          flex,
        }))
        .toSorted((a, b) => a.colId.localeCompare(b.colId)) ?? [],
  },
  columnVisibility: { hiddenColIds: gridState?.columnVisibility?.hiddenColIds.toSorted() ?? [] },
  filter: {
    filterModel: gridState?.filter?.filterModel ?? {},
    advancedFilterModel: gridState?.filter?.advancedFilterModel,
    // Filter UI state (e.g. which filter type is picked), not what the filter applies.
    columnFilterState: undefined,
    selectableFilters: undefined,
  },
  sort: { sortModel: gridState?.sort?.sortModel ?? [] },
});

/** Whether two grid states would display the same table, ignoring differences that don't affect what the user sees. */
export const gridStatesEqual = (a: SavedGridStateJsonString, b: SavedGridStateJsonString) =>
  strongIsEqual(normalizeGridState(parseGridStateJson(a)), normalizeGridState(parseGridStateJson(b)));
